import { Progress as ProgressPrimitive } from '@base-ui/react/progress';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import { forwardRef } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

// ============================================================================
// Variants
// ============================================================================

const progressVariants = cva('', {
  variants: {
    size: {
      sm: 'h-1.5',
      md: 'h-2',
      lg: 'h-3',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const indicatorVariants = cva(
  'rounded-s-full transition-[width] duration-(--duration-slow) ease-(--ease-smooth) motion-reduce:transition-none',
  {
    variants: {
      variant: {
        default: 'bg-primary',
        success: 'bg-success',
        warning: 'bg-warning',
        destructive: 'bg-destructive',
        info: 'bg-info',
        gradient: 'bg-gradient-to-r from-primary/60 to-primary',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const segmentVariants = cva(
  'rounded-full h-4 w-1 transition-[background-color] duration-(--duration-moderate) ease-(--ease-standard) motion-reduce:transition-none',
  {
    variants: {
      variant: {
        default: '',
        success: '',
        warning: '',
        destructive: '',
        info: '',
        gradient: '',
      },
      filled: {
        true: '',
        false: 'bg-primary/20',
      },
    },
    compoundVariants: [
      { variant: 'default', filled: true, className: 'bg-primary' },
      { variant: 'success', filled: true, className: 'bg-success' },
      { variant: 'warning', filled: true, className: 'bg-warning' },
      { variant: 'destructive', filled: true, className: 'bg-destructive' },
      { variant: 'info', filled: true, className: 'bg-info' },
      { variant: 'gradient', filled: true, className: 'bg-primary' },
    ],
    defaultVariants: {
      variant: 'default',
      filled: false,
    },
  },
);

// ============================================================================
// Types
// ============================================================================

export type ProgressType = 'linear' | 'circular' | 'half' | 'segmented';
export type ProgressVariant =
  | 'default'
  | 'success'
  | 'warning'
  | 'destructive'
  | 'info'
  | 'gradient';
export type ProgressSize = 'sm' | 'md' | 'lg';
export type LabelPosition = 'none' | 'inline' | 'tooltip';

export interface ProgressProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Progress type/shape */
  type?: ProgressType;
  /** Current progress value (0-100) */
  value?: number;
  /** Shorthand for type="circular" */
  circular?: boolean;
  /** Number of segments (implies type="segmented") */
  segments?: number;
  /** Color variant */
  variant?: ProgressVariant;
  /** Size */
  size?: ProgressSize;
  /** Label text above progress */
  label?: string;
  /** Sub-label text below progress */
  subLabel?: string;
  /** Show percentage value */
  showValue?: boolean;
  /** Value display position */
  labelPosition?: LabelPosition;
  /** Custom value formatter */
  valueFormat?: (value: number) => string;
  /** Icon prefix for linear */
  icon?: React.ReactNode;
  /** Avatar image URL */
  avatarSrc?: string;
  /** Avatar fallback text */
  avatarFallback?: string;
  /** Circular stroke width */
  strokeWidth?: number;
  /** Custom progress color */
  color?: string;
  /** Custom track color */
  trackColor?: string;
}

// ============================================================================
// Constants & Config
// ============================================================================

const DEFAULT_SEGMENTS = 25;

const CIRCULAR_SIZE_CONFIG = {
  sm: { dimension: 80, strokeWidth: 6, fontSize: 'text-sm' },
  md: { dimension: 100, strokeWidth: 8, fontSize: 'text-base' },
  lg: { dimension: 120, strokeWidth: 10, fontSize: 'text-lg' },
};

/** Maps variant to text color class for labels */
const VARIANT_TEXT_COLORS: Record<ProgressVariant, string> = {
  default: 'text-muted-foreground',
  success: 'text-success',
  warning: 'text-warning',
  destructive: 'text-destructive',
  info: 'text-info',
  gradient: 'text-muted-foreground',
};

/** Maps variant to CSS custom property for SVG strokes */
const VARIANT_CSS_COLORS: Record<ProgressVariant, string> = {
  default: 'var(--primary)',
  success: 'var(--success)',
  warning: 'var(--warning)',
  destructive: 'var(--destructive)',
  info: 'var(--info)',
  gradient: 'var(--primary)',
};

function normalizeProgressValue(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return Math.min(100, Math.max(0, value));
}

function normalizeProgressSegments(segments: number | undefined): number {
  if (!Number.isFinite(segments)) {
    return DEFAULT_SEGMENTS;
  }

  return Math.max(1, Math.floor(segments as number));
}

function normalizeStrokeWidth(
  strokeWidth: number | undefined,
  fallback: number,
): number {
  return typeof strokeWidth === 'number' &&
    Number.isFinite(strokeWidth) &&
    strokeWidth > 0
    ? strokeWidth
    : fallback;
}

function getAvatarInitials(value?: string): string {
  if (!value) {
    return '';
  }

  const parts = value.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  return value.slice(0, 2).toUpperCase();
}

// ============================================================================
// Sub-components
// ============================================================================

function ProgressTrack({
  className,
  size,
  ...props
}: ProgressPrimitive.Track.Props & { size?: ProgressSize }) {
  return (
    <ProgressPrimitive.Track
      className={cn(
        'bg-primary/20 rounded-full relative flex w-full items-center overflow-hidden',
        progressVariants({ size }),
        className,
      )}
      data-slot="progress-track"
      {...props}
    />
  );
}

function ProgressIndicator({
  className,
  variant,
  value,
  ...props
}: ProgressPrimitive.Indicator.Props & {
  variant?: ProgressVariant;
  value?: number;
}) {
  return (
    <ProgressPrimitive.Indicator
      data-slot="progress-indicator"
      className={cn(indicatorVariants({ variant }), className)}
      {...props}
    />
  );
}

function FloatingTooltip({
  value,
  formattedValue,
}: {
  value: number;
  formattedValue: string;
}) {
  return (
    <div
      data-slot="progress-floating-tooltip"
      className="absolute top-full -translate-x-1/2 rtl:translate-x-1/2 z-10 mt-1 transition-[inset-inline-start] duration-(--duration-slow) ease-(--ease-smooth) motion-reduce:transition-none"
      style={{ insetInlineStart: `${Math.max(0, Math.min(100, value))}%` }}
    >
      <div className="flex flex-col items-center">
        <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-b-[6px] border-l-transparent border-r-transparent border-b-foreground" />
        <div className="bg-foreground text-background px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap">
          {formattedValue}
        </div>
      </div>
    </div>
  );
}

function AvatarTooltip({
  value,
  avatarSrc,
  avatarFallback,
}: {
  value: number;
  avatarSrc?: string;
  avatarFallback?: string;
}) {
  return (
    <div
      data-slot="progress-avatar-tooltip"
      className="absolute bottom-full -translate-x-1/2 rtl:translate-x-1/2 z-10 mb-1 transition-[inset-inline-start] duration-(--duration-slow) ease-(--ease-smooth) motion-reduce:transition-none"
      style={{ insetInlineStart: `${Math.max(0, Math.min(100, value))}%` }}
    >
      <div className="flex flex-col items-center">
        <Avatar size="sm" className="size-9 shadow-md after:border-primary">
          {avatarSrc && (
            <AvatarImage src={avatarSrc} alt={avatarFallback ?? ''} />
          )}
          <AvatarFallback>{getAvatarInitials(avatarFallback)}</AvatarFallback>
        </Avatar>
        <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-primary -mt-px" />
      </div>
    </div>
  );
}

// ============================================================================
// Linear Progress
// ============================================================================

interface LinearProgressInternalProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  value: number;
  variant: ProgressVariant;
  size: ProgressSize;
  label?: string;
  subLabel?: string;
  showValue?: boolean;
  labelPosition: LabelPosition;
  formattedValue: string;
  icon?: React.ReactNode;
  avatarSrc?: string;
  avatarFallback?: string;
  ref?: React.Ref<HTMLDivElement>;
}

function LinearProgress({
  value,
  variant,
  size,
  label,
  subLabel,
  showValue,
  labelPosition,
  formattedValue,
  icon,
  avatarSrc,
  avatarFallback,
  className,
  ref,
  ...restProps
}: LinearProgressInternalProps) {
  const hasHeader = label || (showValue && labelPosition === 'inline');
  const showTooltip = labelPosition === 'tooltip';
  const showAvatarOnBar = avatarSrc && !label;

  return (
    <ProgressPrimitive.Root
      ref={ref}
      value={value}
      aria-label={label || `Progress: ${formattedValue}`}
      data-slot="progress"
      className={cn('flex flex-col gap-2 w-full', className)}
      {...restProps}
    >
      {hasHeader && (
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {avatarSrc && label && (
              <Avatar size="xs" className="size-6 shrink-0">
                <AvatarImage src={avatarSrc} alt={avatarFallback ?? ''} />
                <AvatarFallback>
                  {getAvatarInitials(avatarFallback)}
                </AvatarFallback>
              </Avatar>
            )}
            {label && (
              <span className="text-sm font-medium text-foreground truncate">
                {label}
              </span>
            )}
          </div>
          {showValue && labelPosition === 'inline' && (
            <span className="text-sm font-medium text-foreground shrink-0">
              {formattedValue}
            </span>
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        {icon && (
          <div className="shrink-0 flex items-center justify-center size-6 rounded-xl border border-dashed border-muted-foreground p-1">
            {icon}
          </div>
        )}
        <div
          className={cn(
            'relative flex-1',
            (showTooltip || showAvatarOnBar) && 'overflow-visible',
          )}
        >
          {showTooltip && (
            <FloatingTooltip value={value} formattedValue={formattedValue} />
          )}
          {showAvatarOnBar && (
            <AvatarTooltip
              value={value}
              avatarSrc={avatarSrc}
              avatarFallback={avatarFallback}
            />
          )}
          <ProgressTrack size={size}>
            <ProgressIndicator variant={variant} value={value} />
          </ProgressTrack>
        </div>
      </div>

      {subLabel && (
        <span className={cn('text-sm', VARIANT_TEXT_COLORS[variant])}>
          {subLabel}
        </span>
      )}
    </ProgressPrimitive.Root>
  );
}

// ============================================================================
// Segmented Progress
// ============================================================================

interface SegmentedProgressInternalProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  value: number;
  variant: ProgressVariant;
  label?: string;
  showValue?: boolean;
  formattedValue: string;
  segments: number;
  ref?: React.Ref<HTMLDivElement>;
}

function SegmentedProgress({
  value,
  variant,
  label,
  showValue,
  formattedValue,
  segments,
  className,
  ref,
  ...restProps
}: SegmentedProgressInternalProps) {
  const filledSegments = Math.round((value / 100) * segments);
  const labelId = React.useId();

  return (
    <div
      ref={ref}
      data-slot="progress"
      className={cn('flex flex-col gap-2 w-full', className)}
      {...restProps}
    >
      {(label || showValue) && (
        <div className="flex items-center justify-between gap-2">
          {label && (
            <span id={labelId} className="text-sm font-medium text-foreground">
              {label}
            </span>
          )}
          {showValue && (
            <span className="text-sm font-medium text-foreground">
              {formattedValue}
            </span>
          )}
        </div>
      )}
      <div
        className="flex gap-1"
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={formattedValue}
        {...(label
          ? { 'aria-labelledby': labelId }
          : { 'aria-label': `Progress: ${formattedValue}` })}
      >
        {Array.from({ length: segments }).map((_, i) => {
          const isFilled = i < filledSegments;
          return (
            <div
              key={i}
              className={segmentVariants({ variant, filled: isFilled })}
            />
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Circular Progress
// ============================================================================

interface CircularProgressInternalProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children' | 'color'> {
  value: number;
  variant: ProgressVariant;
  size: ProgressSize;
  shape: 'circle' | 'half';
  label?: string;
  showValue?: boolean;
  formattedValue: string;
  avatarSrc?: string;
  avatarFallback?: string;
  strokeWidth?: number;
  color?: string;
  trackColor?: string;
  ref?: React.Ref<HTMLDivElement>;
}

function CircularProgress({
  value,
  variant,
  size,
  shape,
  label,
  showValue,
  formattedValue,
  avatarSrc,
  avatarFallback,
  strokeWidth: customStrokeWidth,
  color,
  trackColor,
  className,
  ref,
  ...restProps
}: CircularProgressInternalProps) {
  const config = CIRCULAR_SIZE_CONFIG[size];
  const strokeWidth = normalizeStrokeWidth(
    customStrokeWidth,
    config.strokeWidth,
  );
  const dimension = config.dimension;
  const padding = strokeWidth;

  const radius = (dimension - padding * 2 - strokeWidth) / 2;
  const center = dimension / 2;
  const circumference = 2 * Math.PI * radius;

  const clampedValue = normalizeProgressValue(value);
  const isHalf = shape === 'half';
  const maxProgress = isHalf ? 0.5 : 1;
  const progressRatio = (clampedValue / 100) * maxProgress;
  const strokeDashoffset = circumference * (1 - progressRatio);
  const trackDashoffset = circumference * (1 - maxProgress);
  const rotation = isHalf ? 180 : -90;

  const viewBoxHeight = isHalf ? dimension / 2 + strokeWidth : dimension;
  const containerHeight = isHalf ? dimension / 2 + strokeWidth : dimension;

  const progressColor = color || VARIANT_CSS_COLORS[variant];

  const bgTrackColor =
    trackColor || 'color-mix(in srgb, var(--primary) 20%, transparent)';

  const avatarSize =
    size === 'sm' ? 'size-10' : size === 'md' ? 'size-12' : 'size-16';

  const circleCy = isHalf ? dimension / 2 : center;
  const svgTransform = `rotate(${rotation} ${center} ${circleCy})`;

  return (
    <div
      ref={ref}
      data-slot="progress"
      className={cn('relative flex items-center justify-center', className)}
      style={{ width: dimension, height: containerHeight }}
      role="progressbar"
      aria-valuenow={clampedValue}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuetext={formattedValue}
      aria-label={label || `Progress: ${formattedValue}`}
      {...restProps}
    >
      <svg
        width={dimension}
        height={containerHeight}
        viewBox={`0 0 ${dimension} ${viewBoxHeight}`}
        className="overflow-visible"
      >
        <circle
          cx={center}
          cy={circleCy}
          r={radius}
          fill="none"
          stroke={bgTrackColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={trackDashoffset}
          transform={svgTransform}
        />
        <circle
          cx={center}
          cy={circleCy}
          r={radius}
          fill="none"
          stroke={progressColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          transform={svgTransform}
          className="transition-[stroke-dashoffset] duration-(--duration-slow) ease-(--ease-smooth) motion-reduce:transition-none"
        />
      </svg>

      <div
        className={cn(
          'absolute flex flex-col items-center justify-center',
          isHalf && 'translate-y-[20%]',
        )}
      >
        {avatarSrc ? (
          <Avatar className={avatarSize}>
            <AvatarImage src={avatarSrc} alt={avatarFallback ?? ''} />
            <AvatarFallback>{getAvatarInitials(avatarFallback)}</AvatarFallback>
          </Avatar>
        ) : (
          showValue && (
            <span
              className={cn(
                'font-medium text-foreground tracking-tight',
                config.fontSize,
              )}
            >
              {formattedValue}
            </span>
          )
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main Progress Component
// ============================================================================

const Progress = forwardRef<HTMLDivElement, ProgressProps>(
  (
    {
      type,
      value = 0,
      circular,
      segments,
      variant = 'default',
      size = 'sm',
      label,
      subLabel,
      showValue = false,
      labelPosition = 'inline',
      valueFormat,
      icon,
      avatarSrc,
      avatarFallback,
      strokeWidth,
      color,
      trackColor,
      className,
      ...props
    },
    ref,
  ) => {
    const normalizedValue = normalizeProgressValue(value);
    const normalizedSegments = normalizeProgressSegments(segments);

    // Type inference
    const resolvedType: ProgressType =
      type ||
      (circular ? 'circular' : segments !== undefined ? 'segmented' : 'linear');

    const formattedValue = valueFormat
      ? valueFormat(normalizedValue)
      : `${Math.round(normalizedValue)}%`;

    if (resolvedType === 'circular' || resolvedType === 'half') {
      return (
        <CircularProgress
          ref={ref}
          value={normalizedValue}
          variant={variant}
          size={size}
          shape={resolvedType === 'half' ? 'half' : 'circle'}
          label={label}
          showValue={showValue}
          formattedValue={formattedValue}
          avatarSrc={avatarSrc}
          avatarFallback={avatarFallback}
          strokeWidth={strokeWidth}
          color={color}
          trackColor={trackColor}
          className={className}
          {...props}
        />
      );
    }

    if (resolvedType === 'segmented') {
      return (
        <SegmentedProgress
          ref={ref}
          value={normalizedValue}
          variant={variant}
          label={label}
          showValue={showValue}
          formattedValue={formattedValue}
          segments={normalizedSegments}
          className={className}
          {...props}
        />
      );
    }

    return (
      <LinearProgress
        ref={ref}
        value={normalizedValue}
        variant={variant}
        size={size}
        label={label}
        subLabel={subLabel}
        showValue={showValue}
        labelPosition={labelPosition}
        formattedValue={formattedValue}
        icon={icon}
        avatarSrc={avatarSrc}
        avatarFallback={avatarFallback}
        className={className}
        {...props}
      />
    );
  },
);
Progress.displayName = 'Progress';

export { indicatorVariants, Progress, progressVariants, segmentVariants };
